import type { OpeningSlot } from '@growup/arena-shared';
import { Button, Checkbox, Field, TimeInput } from '@growup/ui';
import { useState } from 'react';

import {
  ALL_DAYS,
  DEFAULT_HOURS,
  dayName,
  WEEKEND,
  WORK_DAYS,
  nextSlot,
  setDays,
  slotsOf,
  type Hours,
} from './openingHours';

/** Toggle buttons for picking weekdays, with the usual shortcuts. */
function DayPicker({
  value,
  onChange,
  exclude,
}: {
  value: number[];
  onChange: (days: number[]) => void;
  exclude?: number;
}) {
  const days = ALL_DAYS.filter((d) => d !== exclude);
  const toggle = (day: number) =>
    onChange(value.includes(day) ? value.filter((d) => d !== day) : [...value, day].sort());
  const only = (list: number[]) => list.filter((d) => d !== exclude);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Dias">
        {days.map((day) => (
          <Button
            key={day}
            size="sm"
            slanted={false}
            variant={value.includes(day) ? 'primary' : 'secondary'}
            aria-pressed={value.includes(day)}
            aria-label={dayName(day)}
            onClick={() => toggle(day)}
          >
            {dayName(day).slice(0, 3)}
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="ghost" onClick={() => onChange(only(WORK_DAYS))}>
          Dias úteis
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onChange(only(WEEKEND))}>
          Fim de semana
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onChange(only(ALL_DAYS))}>
          Todos
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onChange([])}>
          Nenhum
        </Button>
      </div>
    </div>
  );
}

function HoursInputs({ hours, onChange }: { hours: Hours; onChange: (h: Hours) => void }) {
  return (
    <>
      <Field label="Abre" className="w-36">
        <TimeInput value={hours.opens} onChange={(opens) => onChange({ ...hours, opens })} />
      </Field>
      <Field label="Fecha" className="w-36">
        <TimeInput value={hours.closes} onChange={(closes) => onChange({ ...hours, closes })} />
      </Field>
    </>
  );
}

/** "Aplicar a vários dias": the same hours for every selected day. */
function ApplyToDays({ onApply }: { onApply: (days: number[], hours: Hours) => void }) {
  const [days, setDaysPicked] = useState<number[]>(WORK_DAYS);
  const [hours, setHours] = useState<Hours>(DEFAULT_HOURS);
  return (
    <div className="mb-4 flex flex-col gap-3 border-2 border-gu-line p-3">
      <p className="text-body font-semibold">Aplicar a vários dias</p>
      <DayPicker value={days} onChange={setDaysPicked} />
      <div className="flex flex-wrap items-end gap-2">
        <HoursInputs hours={hours} onChange={setHours} />
        <Button
          variant="secondary"
          disabled={days.length === 0 || !(hours.opens < hours.closes)}
          onClick={() => onApply(days, hours)}
        >
          Aplicar
        </Button>
      </div>
      <p className="text-caption text-gu-ink-muted">
        Substitui o horário dos dias escolhidos. Só fica no site depois de «Guardar».
      </p>
    </div>
  );
}

function DayRow({
  weekday,
  slots,
  onChange,
  onCopy,
}: {
  weekday: number;
  slots: Hours[];
  onChange: (slots: Hours[]) => void;
  onCopy: (days: number[]) => void;
}) {
  const [copying, setCopying] = useState(false);
  const [targets, setTargets] = useState<number[]>([]);
  const name = dayName(weekday);
  const first = slots[0];
  const open = first !== undefined;
  const setSlot = (i: number, h: Hours) => onChange(slots.map((s, j) => (j === i ? h : s)));

  return (
    <li className="flex flex-col gap-2 border-b border-gu-line pb-3 last:border-b-0">
      <div className="flex flex-col gap-x-4 gap-y-2 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="sm:w-28 sm:pb-2">
          <Checkbox
            label={name}
            checked={open}
            onChange={(e) => onChange(e.target.checked ? [DEFAULT_HOURS] : [])}
          />
        </div>
        {/* Largura fixa em ecras grandes: os botoes ficam na mesma coluna em todos os dias,
            tenham um ou dois intervalos. */}
        <div className="flex flex-1 flex-wrap items-end gap-x-4 gap-y-2 lg:w-[46rem] lg:flex-none">
          {open ? (
            slots.map((slot, i) => (
              <div key={i} className="flex flex-wrap items-end gap-2">
                <HoursInputs hours={slot} onChange={(h) => setSlot(i, h)} />
                {i > 0 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mb-1.5"
                    aria-label={`Remover intervalo de ${name}`}
                    onClick={() => onChange(slots.filter((_, j) => j !== i))}
                  >
                    Remover
                  </Button>
                ) : null}
              </div>
            ))
          ) : (
            <p className="pb-2 text-body text-gu-ink-muted">Fechado</p>
          )}
        </div>
        {/* «Copiar para…» vem primeiro para ficar na mesma coluna em todos os dias. */}
        <div className="flex gap-1.5 pb-1.5">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={copying}
            aria-label={`Copiar ${name} para…`}
            onClick={() => setCopying(!copying)}
          >
            Copiar para…
          </Button>
          {first && slots.length < 2 ? (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Juntar intervalo a ${name}`}
              onClick={() => onChange([...slots, nextSlot(first)])}
            >
              + Intervalo
            </Button>
          ) : null}
        </div>
      </div>
      {copying ? (
        <div className="ml-0 flex flex-col gap-2 bg-gu-raised p-3 sm:ml-32">
          <p className="text-body">
            Copiar o horário de {name} ({open ? 'aberto' : 'fechado'}) para:
          </p>
          <DayPicker value={targets} onChange={setTargets} exclude={weekday} />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={targets.length === 0}
              onClick={() => {
                onCopy(targets);
                setCopying(false);
                setTargets([]);
              }}
            >
              Copiar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCopying(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/** The week's opening hours: one row per day, plus ways to fill several days at once. */
export function WeeklyHours({
  value,
  onChange,
}: {
  value: OpeningSlot[];
  onChange: (slots: OpeningSlot[]) => void;
}) {
  return (
    <div>
      <ApplyToDays onApply={(days, hours) => onChange(setDays(value, days, [hours]))} />
      <ul className="flex flex-col gap-3">
        {ALL_DAYS.map((day) => {
          const slots = slotsOf(value, day);
          return (
            <DayRow
              key={day}
              weekday={day}
              slots={slots}
              onChange={(next) => onChange(setDays(value, [day], next))}
              onCopy={(days) => onChange(setDays(value, days, slots))}
            />
          );
        })}
      </ul>
    </div>
  );
}
